/**
 * Instagram Stories Share Button with Image Generation
 * Generates a Story-ready image and shares it to Instagram Stories
 * Based on Nilgiri Marten Storybook implementation
 */

document.addEventListener('DOMContentLoaded', function() {
  // Get all Instagram share buttons
  const instagramShareButtons = document.querySelectorAll('.share-to-instagram');

  instagramShareButtons.forEach(button => {
    button.addEventListener('click', async function(e) {
      e.preventDefault();

      const url = this.getAttribute('data-url');
      const title = this.getAttribute('data-title');
      const imageUrl = this.getAttribute('data-image') || '';

      // Show loading state
      const originalHTML = this.innerHTML;
      this.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512" style="animation: spin 1s linear infinite;"><path d="M304 48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zm0 416a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM48 304a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm464-48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM142.9 437A48 48 0 1 0 75 369.1 48 48 0 1 0 142.9 437zm0-294.2A48 48 0 1 0 75 75a48 48 0 1 0 67.9 67.9zM369.1 437A48 48 0 1 0 437 369.1 48 48 0 1 0 369.1 437z"/></svg> Generating...';
      this.disabled = true;

      try {
        // Generate Instagram Story image
        const storyImageBlob = await generateInstagramStoryImage(title, url, imageUrl);

        // Share using Web Share API with image file (enables Instagram Stories option)
        if (navigator.share && navigator.canShare && storyImageBlob) {
          const shareData = {
            title: title,
            text: `🌶️ ${title}\n\nDiscover authentic spices from Nilgiri Marten Spices:`,
            url: url,
            files: [new File([storyImageBlob], 'nilgiri-marten-story.jpg', { type: 'image/jpeg' })]
          };

          if (navigator.canShare(shareData)) {
            try {
              await navigator.share(shareData);
              this.innerHTML = originalHTML;
              this.disabled = false;
              return;
            } catch (error) {
              if (error.name === 'AbortError') {
                // User cancelled
                this.innerHTML = originalHTML;
                this.disabled = false;
                return;
              }
              console.log('Share with image failed:', error);
            }
          }
        }

        // Fallback: Copy link and show instructions
        fallbackShare(url, title);
      } catch (error) {
        console.error('Error generating Instagram Story image:', error);
        fallbackShare(url, title);
      } finally {
        // Restore button state
        this.innerHTML = originalHTML;
        this.disabled = false;
      }
    });
  });

  /**
   * Generate Instagram Story Image
   * Creates a 1080x1920 image optimized for Instagram Stories
   */
  async function generateInstagramStoryImage(title, url, productImageUrl) {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Instagram Stories dimensions (9:16 aspect ratio)
      canvas.width = 1080;
      canvas.height = 1920;

      // Create spice-themed gradient background
      const gradient = ctx.createRadialGradient(canvas.width/2, canvas.height/2, 0, canvas.width/2, canvas.height/2, Math.max(canvas.width, canvas.height));
      gradient.addColorStop(0, '#8B4513');
      gradient.addColorStop(0.3, '#A0522D');
      gradient.addColorStop(0.6, '#654321');
      gradient.addColorStop(1, '#2F1B14');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add spice texture
      ctx.globalAlpha = 0.15;
      for (let i = 0; i < 80; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const size = Math.random() * 8 + 3;

        ctx.fillStyle = '#2C1810';
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#8B7355';
        ctx.beginPath();
        ctx.arc(x - size/3, y - size/3, size/3, 0, Math.PI * 2);
        ctx.fill();
      }

      for (let i = 0; i < 120; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const size = Math.random() * 3 + 1;
        ctx.fillStyle = '#D2B48C';
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // Load and draw product image if available
      if (productImageUrl) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';

          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = () => resolve(); // Continue even if image fails
            img.src = productImageUrl;
            setTimeout(() => resolve(), 3000); // Timeout after 3 seconds
          });

          if (img.complete && img.naturalHeight !== 0) {
            // Calculate dimensions
            const maxImgWidth = canvas.width - 120;
            const maxImgHeight = canvas.height * 0.50;
            const imgAspectRatio = img.width / img.height;

            let imgWidth, imgHeight;
            if (imgAspectRatio > maxImgWidth / maxImgHeight) {
              imgWidth = maxImgWidth;
              imgHeight = maxImgWidth / imgAspectRatio;
            } else {
              imgHeight = maxImgHeight;
              imgWidth = maxImgHeight * imgAspectRatio;
            }

            const imgX = (canvas.width - imgWidth) / 2;
            const imgY = 250;

            // Add glow effect
            ctx.shadowColor = '#F4A460';
            ctx.shadowBlur = 40;

            // Draw image with rounded corners
            ctx.save();
            ctx.beginPath();
            const radius = 20;
            ctx.moveTo(imgX + radius, imgY);
            ctx.lineTo(imgX + imgWidth - radius, imgY);
            ctx.quadraticCurveTo(imgX + imgWidth, imgY, imgX + imgWidth, imgY + radius);
            ctx.lineTo(imgX + imgWidth, imgY + imgHeight - radius);
            ctx.quadraticCurveTo(imgX + imgWidth, imgY + imgHeight, imgX + imgWidth - radius, imgY + imgHeight);
            ctx.lineTo(imgX + radius, imgY + imgHeight);
            ctx.quadraticCurveTo(imgX, imgY + imgHeight, imgX, imgY + imgHeight - radius);
            ctx.lineTo(imgX, imgY + radius);
            ctx.quadraticCurveTo(imgX, imgY, imgX + radius, imgY);
            ctx.clip();
            ctx.drawImage(img, imgX, imgY, imgWidth, imgHeight);
            ctx.restore();

            // Reset shadow
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;

            // Add border
            ctx.strokeStyle = 'rgba(244, 164, 96, 0.8)';
            ctx.lineWidth = 6;
            ctx.beginPath();
            ctx.moveTo(imgX + radius, imgY);
            ctx.lineTo(imgX + imgWidth - radius, imgY);
            ctx.quadraticCurveTo(imgX + imgWidth, imgY, imgX + imgWidth, imgY + radius);
            ctx.lineTo(imgX + imgWidth, imgY + imgHeight - radius);
            ctx.quadraticCurveTo(imgX + imgWidth, imgY + imgHeight, imgX + imgWidth - radius, imgY + imgHeight);
            ctx.lineTo(imgX + radius, imgY + imgHeight);
            ctx.quadraticCurveTo(imgX, imgY + imgHeight, imgX, imgY + imgHeight - radius);
            ctx.lineTo(imgX, imgY + radius);
            ctx.quadraticCurveTo(imgX, imgY, imgX + radius, imgY);
            ctx.stroke();
          }
        } catch (error) {
          console.log('Could not load product image:', error);
        }
      }

      // Add title
      ctx.fillStyle = '#F5DEB3';
      ctx.font = 'bold 56px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.shadowColor = 'rgba(0,0,0,0.7)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 3;

      // Word wrap title
      const maxWidth = canvas.width - 100;
      const words = title.split(' ');
      let lines = [];
      let currentLine = '';

      for (let word of words) {
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        const metrics = ctx.measureText(testLine);

        if (metrics.width > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }

        if (lines.length >= 3) break;
      }

      if (currentLine && lines.length < 3) {
        lines.push(currentLine);
      }

      // Draw title lines
      const titleStartY = 100;
      lines.forEach((line, index) => {
        ctx.fillText(line, canvas.width / 2, titleStartY + (index * 65));
      });

      // Add brand at bottom
      ctx.fillStyle = '#DAA520';
      ctx.font = 'bold 48px Georgia, serif';
      ctx.shadowBlur = 6;
      ctx.fillText('Nilgiri Marten Spices', canvas.width / 2, canvas.height - 180);

      ctx.fillStyle = '#F5DEB3';
      ctx.font = '36px Georgia, serif';
      ctx.fillText('Authentic Single-Origin Spices', canvas.width / 2, canvas.height - 130);

      // Add swipe up hint
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 32px sans-serif';
      ctx.fillText('👆 Swipe up to shop', canvas.width / 2, canvas.height - 60);

      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;

      // Convert to blob
      return new Promise(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.95);
      });

    } catch (error) {
      console.error('Error generating Instagram Story image:', error);
      return null;
    }
  }

  /**
   * Fallback sharing method for non-supported browsers
   * Opens Instagram web or copies link to clipboard
   */
  function fallbackShare(url, title) {
    // Check if on mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      // Try to open Instagram app (if installed, user can share from there)
      window.location.href = `https://www.instagram.com/`;

      // Also copy link to clipboard
      copyToClipboard(url, title);
    } else {
      // Desktop: just copy to clipboard and show message
      copyToClipboard(url, title);
    }
  }

  /**
   * Copy link to clipboard
   */
  function copyToClipboard(url, title) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => {
          showNotification('Link copied! Paste it in Instagram Stories.');
        })
        .catch(err => {
          console.error('Failed to copy:', err);
          showNotification('Please copy this link manually: ' + url);
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
        showNotification('Link copied! Paste it in Instagram Stories.');
      } catch (err) {
        console.error('Fallback copy failed:', err);
        prompt('Copy this link to share on Instagram:', url);
      }

      document.body.removeChild(textArea);
    }
  }

  // Add CSS for spinning animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);

  /**
   * Show a temporary notification to the user
   */
  function showNotification(message) {
    // Check if there's already a notification element
    let notification = document.getElementById('instagram-share-notification');

    if (!notification) {
      notification = document.createElement('div');
      notification.id = 'instagram-share-notification';
      notification.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: linear-gradient(135deg, #405DE6, #5851DB, #833AB4, #C13584, #E1306C, #FD1D1D);
        color: white;
        padding: 15px 25px;
        border-radius: 50px;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
        z-index: 999999;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
        max-width: 90%;
        animation: slideUp 0.3s ease-out;
      `;
      document.body.appendChild(notification);

      // Add animation
      const style = document.createElement('style');
      style.textContent = `
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `;
      document.head.appendChild(style);
    }

    notification.textContent = message;
    notification.style.display = 'block';

    // Hide after 3 seconds
    setTimeout(() => {
      notification.style.display = 'none';
    }, 3000);
  }
});
